import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { HlmBadgeComponent, HlmBtnDirective, HlmIconComponent } from '../../../ui';
import { CallInformation, callStatusLabel, callStatusTone } from '../../calls/call-information.model';
import { CALL_LOAD_ERROR_TEXT, CALL_NOT_AVAILABLE_TEXT } from '../../calls/call-lookup.service';

/** Lookup state the parent (unified search) drives the card with. */
export type CallCardState = 'loading' | 'found' | 'not-found' | 'error';

/** `card` = unambiguous-track call card; `entry` = compact candidate-track row. */
export type CallCardVariant = 'card' | 'entry';

/** Auto-open delay (U3). */
export const CALL_CARD_COUNTDOWN_MS = 8000;
/** Tick granularity — drives the ring / drain bar; the visible number is whole seconds. */
const TICK_MS = 100;
/** Circumference of the countdown ring (r = 11). */
const RING_CIRCUMFERENCE = 2 * Math.PI * 11;

/** Berlin wall-clock parts, matching the Call Viewer's timezone. */
const BERLIN_PARTS_FMT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Berlin',
  day: '2-digit',
  month: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
/** Fixed month names — ICU's en-GB `short` month varies ("Sep" vs "Sept") across runtimes. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Formats a call timestamp as "22 Sep 2026 · 00:12" (Berlin); unparsable input passes through. */
function formatCallTimestamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const parts = BERLIN_PARTS_FMT.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('day')} ${MONTHS[Number(part('month')) - 1]} ${part('year')} · ${part('hour')}:${part('minute')}`;
}

/**
 * Call result for the unified search (Ctrl+K overlay + landing) when the query is a CCHSID.
 *
 * States (driven by the parent's lookup): `loading` (skeleton), `found`, `not-found` (notice)
 * and `error` (notice + Retry, emits `retry`).
 *
 * - `variant="card"` (unambiguous track, `found`): full card. With `autoOpen` it runs an 8 s
 *   reverse countdown (ring, seconds, drain bar) that emits `open` on expiry and then shows
 *   "Opening Call Details…". The countdown stops for good — it never resumes — on:
 *   real pointer movement over the card (not the synthetic `mouseenter` / zero-movement
 *   `pointermove` a layout shift causes) or `focusin` while it runs, Cancel, or the parent calling
 *   `stopCountdown()` (on typing, ArrowUp/Down and before it opens the call). A stopped card
 *   shows "Auto-open stopped — click the card or press Enter". A click anywhere on the card
 *   (unless text is selected inside it) opens it immediately; the card is keyboard-focusable
 *   (`tabindex="0"`) and Enter / Space on it open it too. The Cancel slot stays rendered
 *   (invisible) after the countdown stops so the layout does not shift under the cursor. If
 *   the parent's navigation fails it calls `markOpenFailed()`, which returns the card from
 *   "Opening Call Details…" to the stopped state.
 * - `variant="entry"` (candidate track, `found`): compact row, no button, no countdown — the
 *   whole row is the click target (click, Enter or Space emits `open`).
 *
 * The interval is owned by this component and torn down on destroy, so a closed overlay can
 * never navigate from a dead component.
 * @ticket EB-4153
 */
@Component({
  selector: 'cmt-unified-call-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmBadgeComponent, HlmBtnDirective, HlmIconComponent],
  host: {
    class: 'block',
    '(pointermove)': 'onPointerMove($event)',
    '(pointerleave)': 'restingPointer = null',
    '(focusin)': 'onFocusIn($event)',
  },
  template: `
    @switch (state()) {
      @case ('loading') {
        <div [class]="shellClass()" aria-busy="true" aria-label="Looking up the call">
          <span [class]="glyphClass()"><hlm-icon name="lucidePhone" size="1.25rem" /></span>
          <div class="flex min-w-0 flex-col gap-2">
            <div class="h-[18px] w-52 max-w-full animate-pulse rounded-md bg-muted motion-reduce:animate-none"></div>
            <div class="h-3.5 w-[26rem] max-w-full animate-pulse rounded-md bg-muted motion-reduce:animate-none"></div>
            <div class="h-3 w-72 max-w-full animate-pulse rounded-md bg-muted motion-reduce:animate-none"></div>
          </div>
        </div>
      }

      @case ('not-found') {
        <div data-testid="call-not-available" class="flex items-center gap-3.5 rounded-xl border border-dashed border-border p-4 text-base text-muted-foreground">
          <span class="grid h-11 w-11 flex-none place-items-center rounded-xl bg-muted text-muted-foreground">
            <hlm-icon name="lucidePhoneOff" size="1.15rem" />
          </span>
          <span class="min-w-0 flex-1 font-medium text-foreground">{{ notAvailableText }}</span>
        </div>
      }

      @case ('error') {
        <div role="alert" class="flex flex-wrap items-center gap-3.5 rounded-xl border border-dashed border-border p-4 text-base text-muted-foreground">
          <span class="grid h-11 w-11 flex-none place-items-center rounded-xl bg-destructive/10 text-destructive">
            <hlm-icon name="lucideTriangleAlert" size="1.15rem" />
          </span>
          <span class="min-w-0 flex-1">
            <span class="font-medium text-foreground">{{ loadErrorText }}</span> Please try again.
          </span>
          <button hlmBtn variant="outline" size="sm" type="button" data-testid="call-error-retry"
            class="h-[44px] w-full sm:h-9 sm:w-auto" (click)="retry.emit()">
            <hlm-icon name="lucideRefreshCw" size="0.9rem" />
            Retry
          </button>
        </div>
      }

      @case ('found') {
        @if (call(); as c) {
          @if (variant() === 'entry') {
            <div data-testid="call-entry" role="button" tabindex="0"
              [attr.aria-label]="'Open call ' + c.cchsid"
              [class]="entryClass()"
              (click)="open.emit()"
              (keydown.enter)="onEntryKey($event)"
              (keydown.space)="onEntryKey($event)">
              <span class="grid h-9 w-9 flex-none place-items-center rounded-lg bg-primary/10 text-primary">
                <hlm-icon name="lucidePhone" size="1rem" />
              </span>
              <div class="flex min-w-0 flex-1 flex-col">
                <span class="truncate text-base font-medium text-foreground">
                  <span class="font-mono-feat">{{ when() }}</span>
                  @if (c.customerId) { · Customer <span class="font-mono-feat">{{ c.customerId }}</span> }
                  @if (c.brand) { · Brand {{ c.brand }} }
                </span>
                <span class="truncate text-sm text-muted-foreground">
                  @if (c.agentName) { Agent {{ c.agentName }} · }<span class="font-mono-feat">{{ c.cchsid }}</span>
                </span>
              </div>
            </div>
          } @else {
            <div #cardEl data-testid="call-card" role="group" tabindex="0"
              [attr.aria-label]="'Call ' + c.cchsid"
              [class]="cardClass()"
              (click)="onCardClick()"
              (keydown.enter)="onCardKey($event)"
              (keydown.space)="onCardKey($event)">
              <span [class]="glyphClass()"><hlm-icon name="lucidePhone" size="1.25rem" /></span>

              <div class="flex min-w-0 flex-col">
                <div class="flex flex-wrap items-baseline gap-x-2.5 gap-y-1.5">
                  <span class="font-display text-lg font-semibold text-foreground">{{ when() }}</span>
                  @if (c.status) {
                    <hlm-badge [class]="statusTone().pillClass">
                      <span [class]="'h-1.5 w-1.5 rounded-full ' + statusTone().dotClass"></span>
                      {{ statusLabel() }}
                    </hlm-badge>
                  }
                </div>
                <div class="mt-1 flex flex-wrap gap-x-3.5 gap-y-1 text-base text-muted-foreground">
                  @if (callerLine(); as line) {
                    <span class="font-mono-feat">
                      @if (line.callingNumber) {
                        <span class="font-medium text-foreground">{{ line.callingNumber }}</span>
                      }
                      @if (line.callingNumber && line.callingFullNumber) { → }
                      {{ line.callingFullNumber }}
                    </span>
                  }
                  @if (c.agentName) {
                    <span>Agent <span class="font-medium text-foreground">{{ c.agentName }}</span></span>
                  }
                  @if (c.customerId || c.brand) {
                    <span>
                      @if (c.customerId) {
                        Customer <span class="font-mono-feat font-medium text-foreground">{{ c.customerId }}</span>
                        @if (c.brand) { · }
                      }
                      @if (c.brand) {
                        Brand <span class="font-medium text-foreground">{{ c.brand }}</span>
                      }
                    </span>
                  }
                </div>
                <div class="font-mono-feat mt-1.5 break-all text-sm text-muted-foreground">{{ c.cchsid }}</div>
              </div>

              <div class="col-span-2 flex flex-col items-stretch gap-2.5 sm:col-span-1 sm:items-end">
                @if (running()) {
                  <div data-testid="call-card-countdown" role="timer"
                    class="flex items-center gap-2 text-sm text-muted-foreground">
                    <span class="relative block h-[28px] w-[28px] flex-none">
                      <svg class="absolute inset-0 -rotate-90" width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
                        <circle cx="14" cy="14" r="11" fill="none" stroke="currentColor" stroke-width="3" class="text-muted" />
                        <circle cx="14" cy="14" r="11" fill="none" stroke="currentColor" stroke-width="3"
                          stroke-linecap="round" class="text-primary motion-reduce:hidden"
                          [attr.stroke-dasharray]="ringCircumference"
                          [attr.stroke-dashoffset]="ringOffset()" />
                      </svg>
                      <span class="font-mono-feat absolute inset-0 flex items-center justify-center text-sm font-semibold leading-none tabular-nums text-primary" aria-hidden="true">{{ secondsLeft() }}</span>
                    </span>
                    <span>Opens automatically in {{ secondsLeft() }} s</span>
                  </div>
                } @else if (expired()) {
                  <div class="flex items-center gap-1.5 text-sm text-muted-foreground" aria-live="polite">
                    <hlm-icon name="lucideArrowRight" size="0.9rem" />
                    Opening Call Details…
                  </div>
                } @else {
                  <div class="text-sm text-muted-foreground sm:text-right" aria-live="polite">
                    Auto-open stopped — click the card or press Enter
                  </div>
                }
                <div class="flex sm:justify-end">
                  <span data-testid="call-card-cancel-slot" class="flex flex-1 sm:flex-none"
                    (click)="$event.stopPropagation()">
                    <button hlmBtn variant="outline" size="sm" type="button" data-testid="call-card-cancel"
                      class="h-[44px] w-full sm:h-9 sm:w-auto" [class.invisible]="!running()"
                      [attr.aria-hidden]="running() ? null : 'true'" [attr.tabindex]="running() ? null : -1"
                      (mousedown)="$event.preventDefault()" (click)="onCancel($event)">Cancel</button>
                  </span>
                </div>
              </div>

              @if (running()) {
                <div class="absolute bottom-0 left-0 h-[3px] bg-primary/75 motion-reduce:hidden"
                  aria-hidden="true" [style.width.%]="remainingPercent()"></div>
              }
            </div>
          }
        }
      }
    }
  `,
})
export class UnifiedCallCardComponent {
  readonly state = input.required<CallCardState>();
  readonly call = input<CallInformation | null>(null);
  readonly variant = input<CallCardVariant>('card');
  readonly autoOpen = input(true);
  readonly selected = input(false);

  /** Open the call (a click on the card, countdown expiry, or an entry click). */
  readonly open = output<void>();
  /** Cancel was activated — the host restores focus to its search input. */
  readonly cancelled = output<void>();
  /** Re-issue the lookup (error state). */
  readonly retry = output<void>();

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  /** Root of the found `card` variant — the keyboard focus target (never the soon-hidden Cancel). */
  private readonly cardEl = viewChild<ElementRef<HTMLElement>>('cardEl');

  protected readonly ringCircumference = RING_CIRCUMFERENCE;
  protected readonly notAvailableText = CALL_NOT_AVAILABLE_TEXT;
  protected readonly loadErrorText = CALL_LOAD_ERROR_TEXT;

  private readonly remainingMs = signal(CALL_CARD_COUNTDOWN_MS);
  /** True while the interval runs. */
  protected readonly running = signal(false);
  /** Set once the countdown has run out and emitted `open`. */
  protected readonly expired = signal(false);
  /** Set once the countdown was stopped or finished — it never starts again (U4). */
  private readonly settledState = signal(false);
  /**
   * True once the countdown was stopped or finished (or stopped before it could start). The parent
   * reads it for its screen-reader announcement of the pending auto-open.
   * @ticket EB-4153
   */
  readonly countdownSettled = this.settledState.asReadonly();
  private intervalId: ReturnType<typeof setInterval> | null = null;
  /** Wall-clock deadline (`Date.now()`-based) the countdown runs against, so a throttled/background
   *  tab still expires after the full countdown of real time instead of the equivalent (throttled) interval ticks. */
  private deadline: number | null = null;

  /** Countdown applies only to the unambiguous-track card with a hit and auto-open enabled (A7). */
  private readonly eligible = computed(() =>
    this.variant() === 'card' && this.state() === 'found' && this.call() !== null && this.autoOpen());

  protected readonly secondsLeft = computed(() => Math.ceil(this.remainingMs() / 1000));
  protected readonly remainingPercent = computed(() => (this.remainingMs() / CALL_CARD_COUNTDOWN_MS) * 100);
  protected readonly ringOffset = computed(() => RING_CIRCUMFERENCE * (1 - this.remainingMs() / CALL_CARD_COUNTDOWN_MS));

  protected readonly when = computed(() => formatCallTimestamp(this.call()?.timestamp));
  /** Caller → called line; omitted entirely when both numbers are empty (no dangling arrow). */
  protected readonly callerLine = computed(() => {
    const c = this.call();
    const callingNumber = c?.callingNumber ?? '';
    const callingFullNumber = c?.callingFullNumber ?? '';
    if (!callingNumber && !callingFullNumber) return null;
    return { callingNumber, callingFullNumber };
  });
  protected readonly statusLabel = computed(() => callStatusLabel(this.call()?.status));
  protected readonly statusTone = computed(() => callStatusTone(this.call()?.status));

  constructor() {
    effect(() => {
      const eligible = this.eligible();
      untracked(() => {
        if (eligible) this.startCountdown();
        else if (this.running()) this.stopCountdown();
      });
    });
    inject(DestroyRef).onDestroy(() => this.clearTimer());
  }

  /** First pointer position observed over the card; a later differing position means real movement. */
  protected restingPointer: { x: number; y: number } | null = null;

  /**
   * Stops the countdown only on REAL pointer movement over the card. Chrome fires `mouseenter`,
   * `pointerover` and a synthetic `pointermove` when a layout change (the landing search box jumping
   * to the top) puts the card under a resting pointer; those carry `movementX/Y === 0` and the same
   * coordinates as the first position seen, so they are ignored. A real move has non-zero
   * `movementX/Y` or coordinates differing from that first position.
   * @ticket EB-4153
   */
  protected onPointerMove(event: PointerEvent): void {
    // Only a RUNNING countdown can be stopped by the pointer (never latch the stop during `loading`).
    if (!this.running()) return;
    const first = this.restingPointer;
    if (first === null) this.restingPointer = { x: event.clientX, y: event.clientY };
    const moved = event.movementX !== 0 || event.movementY !== 0
      || (first !== null && (event.clientX !== first.x || event.clientY !== first.y));
    if (moved) this.stopCountdown();
  }

  /**
   * Focus entering the card stops a RUNNING countdown (keyboard users Tab onto the card itself);
   * focus during `loading` must not pre-stop the card. If focus lands on Cancel it moves to the
   * card: stopping the countdown hides Cancel (`invisible`, `tabindex=-1`), which would otherwise
   * drop focus to <body> and make Enter a no-op.
   * @ticket EB-4153
   */
  protected onFocusIn(event: FocusEvent): void {
    if (this.running()) this.stopCountdown();
    const target = event.target as HTMLElement | null;
    if (target?.getAttribute('data-testid') === 'call-card-cancel') this.cardEl()?.nativeElement.focus();
  }

  /**
   * Stops the auto-open countdown for good (pointer movement, focus, Cancel, typing, ArrowUp/Down).
   * Idempotent; a no-op once stopped or expired.
   */
  stopCountdown(): void {
    this.settledState.set(true);
    this.running.set(false);
    this.clearTimer();
  }

  /**
   * The parent's navigation for the emitted `open` failed: leave "Opening Call Details…" for the
   * stopped state so the operator can click the card or press Enter again. The countdown does
   * not restart. Idempotent.
   * @ticket EB-4153
   */
  markOpenFailed(): void {
    this.stopCountdown();
    this.expired.set(false);
  }

  /** A click anywhere on the found card opens it immediately (U5), like the customer result card. */
  protected onCardClick(): void {
    if (this.hasSelectionInside()) return;
    this.openNow();
  }

  /** True when the user has selected text inside this card (e.g. to copy the cchsid) — such a click must not open. */
  private hasSelectionInside(): boolean {
    const selection = window.getSelection?.();
    if (!selection || selection.toString() === '') return false;
    const host = this.elementRef.nativeElement;
    return (selection.anchorNode !== null && host.contains(selection.anchorNode))
      || (selection.focusNode !== null && host.contains(selection.focusNode));
  }

  /**
   * Cancel stops the countdown and keeps the card; it must not bubble into an open.
   *
   * The Cancel slot stays rendered (hidden, not removed) once the countdown stops. Reaching Cancel
   * is real pointer movement, which stops the countdown; removing the button would shift the layout
   * under the cursor and the click would land on the card body and open the call. The slot wrapper
   * swallows clicks (a `visibility:hidden` button is not hit-tested), so a click there is a no-op.
   * A mouse press on Cancel is default-prevented so the button never takes focus (it turns
   * invisible right after, which would drop focus to <body> and make Enter a no-op). Keyboard focus
   * reaching Cancel stops the countdown and is moved to the card (`onFocusIn`); the `cancelled`
   * output covers activation without focus (e.g. assistive tech).
   * @ticket EB-4153
   */
  protected onCancel(event: Event): void {
    event.stopPropagation();
    this.stopCountdown();
    this.cancelled.emit();
  }

  private openNow(): void {
    this.stopCountdown();
    this.open.emit();
  }

  /**
   * Enter / Space on the focused card opens it — only when the card itself is the target; keys on
   * the Cancel button bubble here and must keep their own meaning.
   * @ticket EB-4153
   */
  protected onCardKey(event: Event): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.openNow();
  }

  protected onEntryKey(event: Event): void {
    event.preventDefault();
    this.open.emit();
  }

  protected shellClass(): string {
    const base = 'relative grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-3 overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm transition-colors sm:grid-cols-[auto_minmax(0,1fr)_auto]';
    return this.selected() ? `${base} bg-primary/10 ring-2 ring-primary/25` : base;
  }

  /** Found card: the shell plus the click affordance (pointer + hover tint), matching the entry row. */
  protected cardClass(): string {
    return `${this.shellClass()} cursor-pointer hover:border-primary/30 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`;
  }

  protected glyphClass(): string {
    return 'grid h-11 w-11 flex-none place-items-center rounded-xl bg-primary/10 text-primary';
  }

  protected entryClass(): string {
    const base = 'flex min-h-[44px] cursor-pointer items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2.5 transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
    return this.selected() ? `${base} bg-primary/10 ring-2 ring-primary/25` : base;
  }

  private startCountdown(): void {
    if (this.settledState() || this.intervalId !== null) return;
    this.deadline = Date.now() + CALL_CARD_COUNTDOWN_MS;
    this.remainingMs.set(CALL_CARD_COUNTDOWN_MS);
    this.running.set(true);
    this.intervalId = setInterval(() => this.tick(), TICK_MS);
  }

  private tick(): void {
    const next = this.deadline === null ? 0 : Math.max(0, this.deadline - Date.now());
    this.remainingMs.set(next);
    if (next > 0) return;
    this.stopCountdown();
    this.expired.set(true);
    this.open.emit();
  }

  private clearTimer(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
